import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { getFileUrl } from '@/lib/file-url';
import {
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateFullFr,
} from '@shared';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { ArrowRight, Calendar, Check, Clock, MapPin, Shield, Trophy, Users } from 'lucide-react';

const STATUS_CONFIG: Record<CompetitionStatus, { badge: string; badgeClass: string; cardClass: string }> = {
  [CompetitionStatus.REGISTRATION_OPEN]: {
    badge: 'Inscriptions ouvertes',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    cardClass: 'bg-white border-slate-200',
  },
  [CompetitionStatus.REGISTRATION_CLOSED]: {
    badge: 'Inscriptions fermées',
    badgeClass: 'bg-amber-100 text-amber-700',
    cardClass: 'bg-white border-amber-200',
  },
  [CompetitionStatus.IN_PROGRESS]: {
    badge: 'En cours',
    badgeClass: 'bg-blue-100 text-blue-700',
    cardClass: 'bg-white border-blue-200',
  },
  [CompetitionStatus.FINISHED]: {
    badge: 'Terminé',
    badgeClass: 'bg-slate-100 text-slate-600',
    cardClass: 'bg-white border-slate-200 opacity-75',
  },
  [CompetitionStatus.CANCELLED]: {
    badge: 'Annulé',
    badgeClass: 'bg-red-100 text-red-700',
    cardClass: 'bg-white border-slate-200 opacity-50',
  },
  [CompetitionStatus.DRAFT]: {
    badge: 'Brouillon',
    badgeClass: 'bg-slate-100 text-slate-500',
    cardClass: 'bg-white border-slate-200',
  },
  [CompetitionStatus.DISABLED_BY_ADMIN]: {
    badge: 'Désactivé',
    badgeClass: 'bg-red-100 text-red-700',
    cardClass: 'bg-white border-slate-200 opacity-50',
  },
};

export default function CompetitionCard({
  c,
  distanceKm,
  isRegistered,
}: {
  c: Competition;
  distanceKm?: number;
  isRegistered?: boolean;
}) {
  const registrationExpired = !!c.registrationEnd && new Date(c.registrationEnd) < new Date();
  const cfg =
    c.status === CompetitionStatus.REGISTRATION_OPEN && registrationExpired
      ? {
          badge: 'Inscriptions fermées',
          badgeClass: 'bg-amber-100 text-amber-700',
          cardClass: 'bg-white border-amber-200',
        }
      : STATUS_CONFIG[c.status];
  const isFull = c.maxTeams !== null && c.currentTeams >= c.maxTeams;
  const progress = c.maxTeams ? Math.round((c.currentTeams / c.maxTeams) * 100) : 0;
  const concourUrl = `${window.location.origin}/concours/${String(c.id)}`;
  const coverFile = c.files?.find(f => f.usage === 'COMPETITION_COVER');

  return (
    <div
      className={`rounded-3xl border transition-all hover:shadow-lg ${cfg.cardClass} flex flex-col sm:flex-row overflow-hidden`}
    >
      {/* Cover image */}
      <Link
        to={ROUTES.CONCOUR.replace(':id', String(c.id))}
        className='sm:w-44 md:w-48 shrink-0 bg-slate-100 sm:aspect-[3/4]'
      >
        {coverFile ? (
          <img src={getFileUrl(coverFile)} alt={c.name} className='w-full h-40 sm:h-full object-cover' />
        ) : (
          <div className='w-full h-40 sm:h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200'>
            <Trophy className='w-10 h-10 text-slate-300' />
          </div>
        )}
      </Link>

      <div className='flex-1 p-6'>
        <div className='flex flex-col md:flex-row md:items-start gap-6'>
          <div className='flex-1'>
            <div className='flex flex-wrap items-center gap-2 mb-3'>
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold ${isFull ? 'bg-red-100 text-red-700' : cfg.badgeClass}`}
              >
                {isFull ? 'Complet' : cfg.badge}
              </span>
              <span className='px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-lg'>
                {c.type ? TYPE_LABELS[c.type] : '—'}
              </span>
              <span className='px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-lg'>
                {c.level ? LEVEL_LABELS[c.level] : '—'}
              </span>
              {c.restrictedToMembers && (
                <span className='flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-semibold rounded-lg'>
                  <Shield className='w-3 h-3' /> Licence requise
                </span>
              )}
              {c.restrictedAffiliates && (
                <span className='flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-lg'>
                  <Users className='w-3 h-3' /> Clubs affiliés
                </span>
              )}
              {distanceKm !== undefined && (
                <span className='flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-semibold rounded-lg'>
                  <MapPin className='w-3 h-3' /> {distanceKm} km
                </span>
              )}
            </div>
            <h3 className='text-xl font-extrabold text-slate-900 mb-2'>
              <Link to={concourUrl}>{c.name}</Link>
            </h3>
            <div className='flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-4'>
              <span className='flex items-center gap-1.5 uppercase'>
                <Calendar className='w-4 h-4' /> {c.date ? formatDateFullFr(c.date) : '—'}
              </span>
              {(c.venue ?? c.city) && (
                <span className='flex items-center gap-1.5'>
                  <MapPin className='w-4 h-4' /> {c.venue ?? c.city}
                </span>
              )}
            </div>
            {c.maxTeams && (
              <div className='flex items-center gap-4'>
                <div className='flex items-center gap-1.5 text-sm'>
                  <Users className='w-4 h-4 text-blue-500' />
                  <span className={`font-semibold ${isFull ? 'text-red-600' : 'text-slate-700'}`}>
                    {c.currentTeams}/{c.maxTeams}
                  </span>
                  <span className='text-slate-400'>équipes</span>
                </div>
                <div className='flex-1 max-w-32 bg-slate-200 rounded-full h-2'>
                  <div
                    className={`rounded-full h-2 transition-all ${progress >= 90 ? 'bg-red-500' : progress >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className='flex flex-col items-end gap-3 shrink-0'>
            <div className='text-right'>
              {c.price ? (
                <>
                  <div className='text-3xl font-extrabold text-slate-900'>{(c.price / 100).toFixed(0)}€</div>
                  <div className='text-sm text-slate-400'>par équipe</div>
                </>
              ) : (
                <div className='text-2xl font-extrabold text-emerald-600'>Gratuit</div>
              )}
            </div>
            <div className='flex gap-2'>
              <Link
                to={ROUTES.CONCOUR.replace(':id', String(c.id))}
                className='px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200 transition-colors'
              >
                Voir détails
              </Link>
              {c.status === CompetitionStatus.REGISTRATION_OPEN &&
                !isFull &&
                !registrationExpired &&
                (isRegistered ? (
                  <span className='px-5 py-2.5 bg-emerald-100 text-emerald-700 rounded-xl text-sm font-semibold flex items-center gap-1.5'>
                    <Check className='w-4 h-4' /> Déjà inscrit
                  </span>
                ) : (
                  <Link
                    to={`${import.meta.env.VITE_URL_FRONT}${ROUTES.REGISTER_CONCOUR}`.replace(':id', String(c.id))}
                    className='px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-1.5'
                  >
                    S'inscrire <ArrowRight className='w-4 h-4' />
                  </Link>
                ))}
              {c.status === CompetitionStatus.IN_PROGRESS && (
                <button
                  className='px-5 py-2.5 bg-blue-50 text-blue-700 rounded-xl text-sm font-semibold flex items-center gap-1.5'
                  disabled
                >
                  <Clock className='w-4 h-4' /> En cours
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
